import { useLayoutEffect, useState } from 'react';
import { useBoolean } from 'usehooks-ts';
import { calculateImageDimension } from '@/components/common/media-content/image-dimension';
import { getPosterUrl } from '@/utils/media-url';
import { canParseAsUrl } from '@/utils/parse-url';
import { useSafeMediaUrl } from './use-safe-media-url';
import useUserPlatform from './use-user-platform';

type Dimension = [null, null, null] | [number, number, number];

export const useCalculateVideoPosterLayout = ({ src, poster }: { src?: string; poster?: boolean | string }) => {
  const { isDesktop } = useUserPlatform();
  const { src: safeSrc } = useSafeMediaUrl(src);
  const [dimension, setDimension] = useState<Dimension>([null, null, null]);
  const [duration, setDuration] = useState(0);

  const { value: hasPoster, setFalse: setHasNoPoster } = useBoolean(Boolean(poster));
  const posterUrl = typeof poster === 'string' ? poster : poster ? getPosterUrl(safeSrc) : undefined;

  useLayoutEffect(() => {
    if (!posterUrl || !canParseAsUrl(posterUrl) || posterUrl.startsWith('blob:') || posterUrl.startsWith('data:')) {
      setHasNoPoster();
      return;
    }

    const { searchParams } = new URL(posterUrl);
    if (searchParams.has('h')) {
      const width = Number(searchParams.get('w') || 0);
      const height = Number(searchParams.get('h') || 0);
      setDimension(
        calculateImageDimension(width, height, {
          maxWidth: isDesktop ? 380 : 0.45,
          maxHeight: isDesktop ? 0.4 : 0.25,
        }),
      );
    } else {
      setHasNoPoster();
    }
    if (searchParams.has('t')) {
      const time = Number(searchParams.get('t') || 0);
      setDuration(time);
    }
  }, [posterUrl, isDesktop, setHasNoPoster]);

  return {
    dimension,
    setDimension,
    duration,
    setDuration,
    hasPoster,
    safeSrc,
    posterUrl,
    setHasNoPoster,
  };
};
